import {
  Alert, Box, Button, Card, CardActions, CardContent, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, MenuItem, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import { Add, Delete, Edit, Assignment } from '@mui/icons-material';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, extractError } from '../../../shared/api';
import type { Assessment, Class, Subject } from '../../../shared/types';
import { useAuthStore } from '../../../features/auth/store';
import PageHeader from '../../../shared/PageHeader';
import { formatDate } from '../../../shared/utils';

export default function AssessmentsPage() {
  const user = useAuthStore((s) => s.user);
  const isTeacher = user?.role === 'TEACHER';
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Assessment | null>(null);
  const [creating, setCreating] = useState(false);

  const list = useQuery({ queryKey: ['assessments'], queryFn: async () => (await api.get<Assessment[]>('/assessments')).data });
  const classesQ = useQuery({ queryKey: ['classes'], queryFn: async () => (await api.get<Class[]>('/classes')).data });
  const subjectsQ = useQuery({ queryKey: ['subjects'], queryFn: async () => (await api.get<Subject[]>('/subjects')).data });

  const classMap = new Map((classesQ.data ?? []).map((c) => [c.id, `${c.name}-${c.section}`]));
  const subjMap = new Map((subjectsQ.data ?? []).map((s) => [s.id, s.name]));

  const del = useMutation({
    mutationFn: async (id: string) => (await api.delete(`/assessments/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['assessments'] }),
  });

  const today = new Date().toISOString().slice(0, 10);

  return (
    <Box>
      <PageHeader
        title="Assessments"
        subtitle={isTeacher ? 'Create and manage assignments for your classes.' : 'Upcoming assignments and homework.'}
        action={isTeacher && <Button startIcon={<Add />} variant="contained" onClick={() => setCreating(true)}>New assessment</Button>}
      />

      <Stack spacing={2}>
        {(list.data ?? []).map((a) => {
          const overdue = a.dueDate < today;
          return (
            <Card key={a.id}>
              <CardContent>
                <Stack direction={{ sm: 'row' }} justifyContent="space-between" spacing={1}>
                  <Box>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      <Chip size="small" color="primary" label={subjMap.get(a.subjectId) ?? a.subjectId} />
                      <Chip size="small" label={classMap.get(a.classId) ?? a.classId} />
                      <Chip size="small" color={overdue ? 'error' : 'success'} label={`Due ${formatDate(a.dueDate)}`} />
                    </Stack>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>{a.title}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{a.description}</Typography>
                  </Box>
                </Stack>
              </CardContent>
              {isTeacher && (
                <CardActions sx={{ px: 2, pb: 2 }}>
                  <Tooltip title="Edit"><IconButton size="small" onClick={() => setEditing(a)}><Edit /></IconButton></Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small" color="error" onClick={() => { if (window.confirm('Delete?')) del.mutate(a.id); }}>
                      <Delete />
                    </IconButton>
                  </Tooltip>
                </CardActions>
              )}
            </Card>
          );
        })}
        {(list.data?.length ?? 0) === 0 && (
          <Card sx={{ p: 6, textAlign: 'center' }}>
            <Assignment sx={{ fontSize: 48, color: 'text.disabled' }} />
            <Typography sx={{ mt: 2 }} color="text.secondary">No assessments yet.</Typography>
          </Card>
        )}
      </Stack>

      <AssessmentForm
        open={creating || !!editing}
        assessment={editing}
        classes={classesQ.data ?? []}
        subjects={subjectsQ.data ?? []}
        onClose={() => { setCreating(false); setEditing(null); }}
      />
    </Box>
  );
}

function AssessmentForm({ open, assessment, classes, subjects, onClose }: { open: boolean; assessment: Assessment | null; classes: Class[]; subjects: Subject[]; onClose: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState<Partial<Assessment>>(assessment ?? {});
  const isEdit = !!assessment;
  const set = (k: keyof Assessment, v: any) => setForm((f) => ({ ...f, [k]: v }));

  const save = useMutation({
    mutationFn: async () => {
      if (isEdit) return (await api.put(`/assessments/${assessment!.id}`, form)).data;
      return (await api.post('/assessments', form)).data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['assessments'] }); onClose(); setForm({}); },
  });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" TransitionProps={{ onEntered: () => setForm(assessment ?? {}) }}>
      <DialogTitle>{isEdit ? 'Edit assessment' : 'New assessment'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Title" required value={form.title ?? ''} onChange={(e) => set('title', e.target.value)} />
          <TextField label="Description" multiline minRows={3} value={form.description ?? ''} onChange={(e) => set('description', e.target.value)} />
          <Stack direction={{ sm: 'row' }} spacing={2}>
            <TextField select label="Class" required value={form.classId ?? ''} onChange={(e) => set('classId', e.target.value)} sx={{ flex: 1 }}>
              {classes.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}-{c.section}</MenuItem>)}
            </TextField>
            <TextField select label="Subject" required value={form.subjectId ?? ''} onChange={(e) => set('subjectId', e.target.value)} sx={{ flex: 1 }}>
              {subjects.map((s) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
            </TextField>
          </Stack>
          <TextField type="date" label="Due date" required InputLabelProps={{ shrink: true }} value={form.dueDate ?? ''} onChange={(e) => set('dueDate', e.target.value)} />
          {save.isError && <Alert severity="error">{extractError(save.error)}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => save.mutate()} disabled={save.isPending}>
          {save.isPending ? 'Saving…' : isEdit ? 'Save' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
